import {mutate, Arguments} from 'swr'

import {
  createUseMany,
  createUseOne,
  UseManyOptions,
  UseOneOptions
} from './hooks'
import ClientCollection from './client-collection'

/**
 * Helper that creates a matching function that compares against a passed in `stringKey` for use
 * with `mutate`. We use both single strings and arrays keys but are always comparing against the
 * first found string in this matcher.
 * @param matchesKey
 * @returns matcher function
 */
function createMatcher(matchesKey: (stringKey: string) => boolean) {
  function matchArgs(argsKey: Arguments): boolean {
    return Array.isArray(argsKey)
      ? matchArgs(argsKey.find((k) => typeof k === 'string'))
      : typeof argsKey === 'string'
      ? matchesKey(argsKey)
      : false
  }
  return matchArgs
}

/**
 * Extends the `ClientCollection` to work with SWR. Handles revalidating data and
 * generating hooks for usage within components.
 *
 * By manually using `mutate` we are able to avoid unnecessary revalidation.
 */
export default class SwrCollection<
  T extends CL.IModel = CL.IModel
> extends ClientCollection<T> {
  /**
   * Mutate records directly in the cache. Does not do any revalidation.
   */
  async _mutateRecord(_id: string, record?: T) {
    const url = this.getUrl(_id)
    await Promise.all([
      // Replace or remove the value from the cache.
      mutate<T>(
        createMatcher((key) => key === url),
        record,
        {revalidate: false}
      ),

      // Replace or remove the value from any lists it might be in.
      mutate<T[]>(
        createMatcher((key) => key.startsWith(`${this.baseUrl}?`)),
        (data) => {
          if (record) {
            return data?.map((d) => (d._id === _id ? record : d))
          } else {
            return data?.filter((d) => d._id !== _id)
          }
        },
        {revalidate: false}
      )
    ])
  }

  /**
   * Clear all references to this ID.
   */
  async clearId(_id: string) {
    // Remove the value from the cache
    await this._mutateRecord(_id)
  }

  /**
   * Force a revalidation of all data retrieved for this collection.
   */
  revalidateGroup() {
    return mutate<T>(
      createMatcher((key) => key.startsWith(`${this.baseUrl}?`)),
      (data) => data,
      {revalidate: true}
    )
  }

  /**
   * Fetch a value and add it to the global cache.
   */
  async get(_id: string): Promise<T> {
    const data = await super.get(_id)
    await this._mutateRecord(_id, data)
    return data
  }

  /**
   * Create a record and trigger a revalidation for related lists.
   */
  async create(properties: Partial<T>): Promise<T> {
    const data = await super.create(properties)
    await this.revalidateGroup()
    return data
  }

  /**
   * Remove via API and revalidate lists.
   */
  async remove(_id: string): Promise<void> {
    await super.remove(_id)
    // Remove the value from the cache
    await this.clearId(_id)
  }

  /**
   * Replace a record and revalidate.
   */
  async put(newProperties: T): Promise<T> {
    const _id = newProperties._id
    const updatedData = await super.updateOne(_id, newProperties)
    await this._mutateRecord(_id, updatedData)
    return updatedData
  }

  /**
   * Update a record and revalidate. Note: this still uses HTTP PUT
   */
  async patch(
    _id: string,
    nonce: string,
    newProperties: Partial<T>
  ): Promise<T> {
    const updatedData = await super.updateOne(_id, {nonce, ...newProperties})
    await this._mutateRecord(_id, updatedData)
    return updatedData
  }

  /**
   * Update a list of records and revalidate.
   */
  async updateMany(newValues: T[]) {
    const updatedData = await super.updateMany(newValues)

    // Update all cached values together
    const updates: Promise<void>[] = []
    for (const data of updatedData) {
      updates.push(this._mutateRecord(data._id, data))
    }
    await Promise.all(updates)

    return updatedData
  }

  /**
   * Helper for creating a `useMany` hook from this collection
   */
  createUseMany(opts?: UseManyOptions<T>) {
    return createUseMany<T>(this.baseUrl, opts)
  }

  /**
   * Helper for creating a `useOne` hook from this collection.
   */
  createUseOne(opts?: UseOneOptions<T>) {
    return createUseOne<T>(this.baseUrl, opts)
  }
}
